import {
  type Filter,
  type FilterValue,
  type Filters,
  type TableColumn,
  type TableFilter,
  getFilterTupleOperand,
  getFilterTupleOperator,
  isLabelFilterArray,
  isLabelsTableFilter,
  isObjectTableFilter,
  labelFiltersToDisplayTokens,
} from '@ipfabric/utils';
import {
  type AllowedOperatorReadable,
  getDefaultOperator,
  getReadableFromOperator,
} from 'components/Table/components/SimpleFilter/helpers/filterOperators';
import type { SimpleFilterField } from 'components/Table/components/SimpleFilter/types/SimpleFilterField';
import { formatFilterValue } from '../components/SimpleFilter/helpers/formatFilterValue';

const getInitialChild = (
  column: TableColumn<any>,
  initialValue: Filter | undefined,
): string | undefined | null => {
  if (!column.filter || !isObjectTableFilter(column.filter)) {
    return undefined;
  }
  if (!initialValue) {
    return null;
  }
  if (column.array) {
    return initialValue[1] as string;
  }
  return initialValue[0];
};

const getReadableOperatorForSimpleFilter = (
  operator: FilterValue | FilterValue[],
  defaultOperator: string,
): AllowedOperatorReadable | '' =>
  operator === defaultOperator
    ? ''
    : getReadableFromOperator(operator as string);

const getObjectFilterInitialValue = (
  filter: TableFilter,
  initialValue: Filter,
) => {
  const filterValue = `${getFilterTupleOperand(initialValue, 1)}`;
  const filterOperator = getFilterTupleOperand(initialValue, 2);
  if (filterOperator === undefined) {
    return '';
  }

  return `${getReadableOperatorForSimpleFilter(
    filterOperator,
    getDefaultOperator(filter, filterValue),
  )}${filterValue}`;
};

const getLabelsFilterInitialValue = (initialValue: Filter): string | null => {
  const operator = getFilterTupleOperator(initialValue);
  const operand = initialValue[initialValue.length - 1];

  // Quick filter maps to includes (containsAny) with display tokens.
  if (operator !== 'containsAny' || !isLabelFilterArray(operand)) {
    return null;
  }

  if (operand.length === 0) {
    return '';
  }

  return labelFiltersToDisplayTokens(operand);
};

const getOtherFilterInitialValue = (
  column: TableColumn<any>,
  initialValue: Filter,
  oldFilterField: SimpleFilterField | undefined,
) => {
  if (!column.filter) {
    return null;
  }

  if (isLabelsTableFilter(column.filter)) {
    return getLabelsFilterInitialValue(initialValue);
  }

  const filterOperator = column.array
    ? getFilterTupleOperand(initialValue, 2)
    : getFilterTupleOperator(initialValue);
  const filterValue = getFilterTupleOperand(initialValue, 1);

  if (filterOperator === undefined || filterValue === undefined) {
    return '';
  }

  return `${getReadableOperatorForSimpleFilter(filterOperator, getDefaultOperator(column.filter, `${filterValue}`))}${formatFilterValue(
    filterValue,
    column.filter,
    oldFilterField,
  )}`;
};

const createFilterField = (
  column: TableColumn<any>,
  initialValue: Filter | undefined,
  oldFilterField?: SimpleFilterField,
): SimpleFilterField | null => {
  if (!column.filter) {
    return null;
  }

  const filterIsColor = initialValue?.[0] === 'color';

  const value = (() => {
    // This function populates value inside `state.simpleFilterFields`.
    // Value of intent filter (color) is stored in `state.intentFilters`,
    // therefore for "filter value" we return null for color filter there.
    if (initialValue && !filterIsColor) {
      if (column.filter.type === 'object') {
        return getObjectFilterInitialValue(column.filter, initialValue);
      }
      return getOtherFilterInitialValue(column, initialValue, oldFilterField);
    }

    return null;
  })();

  return {
    column: column.key,
    filter: column.filter,
    format: column.format,
    value,
    error: false,
    array: !!column.array,
    child: getInitialChild(column, initialValue),
  };
};
export const createSimpleFilterFieldsFromColumns = (
  columns: TableColumn<any>[],
  initialFilters?: Filters,
  oldSimpleFilterFields?: Record<string, SimpleFilterField>,
): Record<string, SimpleFilterField> =>
  columns.reduce<Record<string, SimpleFilterField>>((acc, column) => {
    const filterField = createFilterField(
      column,
      (initialFilters as any)?.[column.key],
      oldSimpleFilterFields?.[column.key],
    );

    if (filterField) {
      acc[filterField.column] = filterField;
    }

    return acc;
  }, {});
